import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Container, Card, Table, Button, ButtonGroup, Modal, Form, Row, Col, Badge, Spinner, Alert, Tabs, Tab
} from 'react-bootstrap';
import { useLocation } from 'react-router-dom';
import Header from '../layouts/Header';
import axiosInstance from '../services/axiosInterceptor';

// สามแบบที่โรงเรียนเก็บค่าหอ — ชื่อบนหน้าจอต้องตรงกับที่เจ้าหน้าที่เรียกกันจริง
// ("ครอบครอง" คือคำที่ใช้เรียกการอยู่ฟรีแล้วจ่ายค่าน้ำ/ค่าไฟตามจริง)
const MODES = {
  free: { label: 'อยู่ฟรี', bg: 'secondary', hint: 'ไม่มีบรรทัดค่าหอในบิล' },
  metered: { label: 'ครอบครอง', bg: 'info', hint: 'อยู่ฟรี แต่เก็บค่าน้ำ/ค่าไฟตามจริง' },
  term: { label: 'รายภาคเรียน', bg: 'primary', hint: 'ราคาคงที่ต่อภาคเรียน แบ่งชำระรายเดือนได้' }
};

const baht = (n) => Number(n || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 });

const emptyRateForm = {
  village_id: null, dorm_id: null, scopeLabel: '',
  charge_mode: 'term', amount: '', tb_fee_item_item_id: '', rate_note: ''
};

const emptyOverrideForm = {
  tb_student_st_id: '', studentLabel: '',
  charge_mode: 'free', override_amount: '', override_reason: ''
};

const FeeDormFees = () => {
  const location = useLocation();
  const [terms, setTerms] = useState([]);
  const [selectedTerm, setSelectedTerm] = useState(location.state?.termId || '');
  const [items, setItems] = useState([]);
  const [tree, setTree] = useState([]);
  const [preview, setPreview] = useState({ summary: null, by_building: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  // แท็บอัตรา — เขตยุบไว้เป็นค่าเริ่มต้น (37 เขต 887 อาคาร กางหมดแล้วหาเขตไม่เจอ)
  const [showRate, setShowRate] = useState(false);
  const [rateForm, setRateForm] = useState(emptyRateForm);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(() => new Set());
  const [genderFilter, setGenderFilter] = useState('all');

  // แท็บค่าตามจริง
  const [usageDorm, setUsageDorm] = useState('');
  const [usageLabel, setUsageLabel] = useState('ค่าไฟ');
  const [usageRows, setUsageRows] = useState([]);
  const [usageDraft, setUsageDraft] = useState({});
  const [usageLoading, setUsageLoading] = useState(false);

  // แท็บยกเว้นรายคน
  const [overrides, setOverrides] = useState([]);
  const [showOverride, setShowOverride] = useState(false);
  const [overrideForm, setOverrideForm] = useState(emptyOverrideForm);
  const [residentSearch, setResidentSearch] = useState('');
  const [residents, setResidents] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [termRes, itemRes] = await Promise.all([
          axiosInstance.get('/api/fee/terms'),
          axiosInstance.get('/api/fee/items', { params: { activeOnly: true } })
        ]);
        if (termRes.data.success) {
          setTerms(termRes.data.data);
          if (!location.state?.termId && termRes.data.data.length > 0) {
            setSelectedTerm(termRes.data.data[0].term_id);
          }
        }
        if (itemRes.data.success) setItems(itemRes.data.data);
      } catch (err) {
        setError(err.response?.data?.message || 'โหลดข้อมูลตั้งต้นไม่สำเร็จ');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchRates = useCallback(async (termId) => {
    if (!termId) { setTree([]); setPreview({ summary: null, by_building: [] }); return; }
    setLoading(true);
    setError('');
    try {
      const res = await axiosInstance.get(`/api/fee/terms/${termId}/dorm/rates`);
      if (res.data.success) {
        setTree(res.data.data.villages);
        setPreview({ summary: res.data.data.summary, by_building: res.data.data.by_building });
      }
    } catch (err) {
      setError(err.response?.data?.message || 'โหลดอัตราค่าหอไม่สำเร็จ');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchOverrides = useCallback(async (termId) => {
    if (!termId) { setOverrides([]); return; }
    try {
      const res = await axiosInstance.get(`/api/fee/terms/${termId}/dorm/overrides`);
      if (res.data.success) setOverrides(res.data.data);
    } catch (err) {
      setError(err.response?.data?.message || 'โหลดรายการยกเว้นไม่สำเร็จ');
    }
  }, []);

  useEffect(() => {
    fetchRates(selectedTerm);
    fetchOverrides(selectedTerm);
    setUsageDorm('');
    setUsageRows([]);
  }, [selectedTerm, fetchRates, fetchOverrides]);

  const termLabel = useMemo(() => {
    const t = terms.find((x) => String(x.term_id) === String(selectedTerm));
    return t ? `${t.year_year} / ภาค ${t.semester}` : '';
  }, [terms, selectedTerm]);

  // อาคารที่คิดแบบครอบครองเท่านั้นที่ต้องกรอกค่าตามจริง — ไล่ตามลำดับเดียวกับฝั่ง
  // server (อัตราของอาคารทับอัตราของเขต) ไม่งั้นหน้าจอจะชวนให้กรอกอาคารที่ไม่ได้ใช้
  const meteredBuildings = useMemo(() => {
    const out = [];
    tree.forEach((v) => {
      v.buildings.forEach((b) => {
        const mode = b.rate ? b.rate.charge_mode : (v.rate ? v.rate.charge_mode : null);
        if (mode === 'metered') out.push({ ...b, village_name: v.village_name });
      });
    });
    return out;
  }, [tree]);

  // เขตชายกับเขตหญิงใช้เลขซ้ำกัน (เขต 3 มีทั้งสองฝั่ง) การกรองเพศจึงไม่ใช่แค่ย่อรายการ
  // แต่ตัดความกำกวมว่ากำลังตั้งราคาให้เขต 3 อันไหนอยู่
  const visibleTree = useMemo(
    () => (genderFilter === 'all' ? tree : tree.filter((v) => v.village_gender === genderFilter)),
    [tree, genderFilter]
  );

  const genderCounts = useMemo(() => ({
    all: tree.length,
    m: tree.filter((v) => v.village_gender === 'm').length,
    f: tree.filter((v) => v.village_gender === 'f').length
  }), [tree]);

  const toggleVillage = (villageId) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(villageId)) next.delete(villageId);
      else next.add(villageId);
      return next;
    });
  };

  // กางเฉพาะเขตที่มองเห็นอยู่ — กางเขตที่ถูกกรองออกไปด้วยจะทำให้พอกดกลับไป
  // "ทั้งหมด" แล้วเจอตารางกางหมดโดยไม่ได้สั่ง
  const expandAll = () => setExpanded(new Set(visibleTree.map((v) => v.village_id)));
  const collapseAll = () => setExpanded(new Set());

  // ---------- อัตรา ----------
  const openRate = (scope) => {
    const existing = scope.rate;
    setRateForm({
      village_id: scope.village_id || null,
      dorm_id: scope.dorm_id || null,
      scopeLabel: scope.label,
      charge_mode: existing ? existing.charge_mode : 'term',
      amount: existing && existing.charge_mode === 'term' ? existing.amount : '',
      tb_fee_item_item_id: existing ? (existing.tb_fee_item_item_id || '') : '',
      rate_note: existing ? (existing.rate_note || '') : ''
    });
    setShowRate(true);
  };

  const saveRate = async () => {
    setSaving(true);
    setError('');
    try {
      await axiosInstance.post(`/api/fee/terms/${selectedTerm}/dorm/rates`, {
        village_id: rateForm.village_id,
        dorm_id: rateForm.dorm_id,
        charge_mode: rateForm.charge_mode,
        amount: rateForm.charge_mode === 'term' ? Number(rateForm.amount) || 0 : 0,
        tb_fee_item_item_id: rateForm.tb_fee_item_item_id || null,
        rate_note: rateForm.rate_note || null
      });
      setShowRate(false);
      fetchRates(selectedTerm);
    } catch (err) {
      setError(err.response?.data?.message || 'บันทึกอัตราไม่สำเร็จ');
    } finally {
      setSaving(false);
    }
  };

  const deleteRate = async (rateId, label) => {
    if (!window.confirm(`ลบอัตราของ ${label}?\nถ้าเป็นอัตราของอาคาร จะกลับไปใช้อัตราของเขตแทน`)) return;
    try {
      await axiosInstance.delete(`/api/fee/dorm/rates/${rateId}`);
      fetchRates(selectedTerm);
    } catch (err) {
      setError(err.response?.data?.message || 'ลบไม่สำเร็จ');
    }
  };

  // ---------- ค่าตามจริง ----------
  const fetchUsage = async (dormId, label) => {
    if (!dormId) { setUsageRows([]); return; }
    setUsageLoading(true);
    try {
      const res = await axiosInstance.get(`/api/fee/terms/${selectedTerm}/dorm/usage/${dormId}`);
      if (res.data.success) {
        setUsageRows(res.data.data);
        const draft = {};
        res.data.data.forEach((s) => {
          const hit = s.items.find((i) => i.usage_label === label);
          draft[s.st_id] = hit ? String(hit.amount) : '';
        });
        setUsageDraft(draft);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'โหลดรายชื่อผู้พักไม่สำเร็จ');
    } finally {
      setUsageLoading(false);
    }
  };

  const saveUsage = async () => {
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const entries = usageRows.map((s) => ({ st_id: s.st_id, amount: usageDraft[s.st_id] || 0 }));
      const res = await axiosInstance.post(
        `/api/fee/terms/${selectedTerm}/dorm/usage/${usageDorm}`,
        { usage_label: usageLabel, entries }
      );
      if (res.data.success) {
        const { saved, removed } = res.data.data;
        setNotice(`บันทึก ${usageLabel} แล้ว ${saved} คน${removed ? ` · ลบรายการที่ไม่ได้กรอก ${removed} คน` : ''}`);
      }
      fetchUsage(usageDorm, usageLabel);
      fetchRates(selectedTerm);
    } catch (err) {
      setError(err.response?.data?.message || 'บันทึกค่าตามจริงไม่สำเร็จ');
    } finally {
      setSaving(false);
    }
  };

  const fillAll = () => {
    const value = window.prompt(`ใส่ ${usageLabel} เท่ากันทุกคนในอาคารนี้ (บาท)`);
    if (value === null) return;
    const next = {};
    usageRows.forEach((s) => { next[s.st_id] = value; });
    setUsageDraft(next);
  };

  // ---------- ยกเว้นรายคน ----------
  const searchResidents = async () => {
    try {
      const res = await axiosInstance.get('/api/fee/dorm/residents', {
        params: { search: residentSearch }
      });
      if (res.data.success) setResidents(res.data.data);
    } catch (err) {
      setError(err.response?.data?.message || 'ค้นหาไม่สำเร็จ');
    }
  };

  const saveOverride = async () => {
    if (!overrideForm.tb_student_st_id) { setError('กรุณาเลือกนักเรียน'); return; }
    setSaving(true);
    setError('');
    try {
      await axiosInstance.post(`/api/fee/terms/${selectedTerm}/dorm/overrides`, {
        tb_student_st_id: overrideForm.tb_student_st_id,
        charge_mode: overrideForm.charge_mode,
        override_amount: overrideForm.charge_mode === 'term' ? overrideForm.override_amount : null,
        override_reason: overrideForm.override_reason
      });
      setShowOverride(false);
      setResidents([]);
      setResidentSearch('');
      fetchOverrides(selectedTerm);
      fetchRates(selectedTerm);
    } catch (err) {
      setError(err.response?.data?.message || 'บันทึกไม่สำเร็จ');
    } finally {
      setSaving(false);
    }
  };

  const deleteOverride = async (row) => {
    if (!window.confirm(`ยกเลิกการยกเว้นของ ${row.st_name}?`)) return;
    try {
      await axiosInstance.delete(`/api/fee/dorm/overrides/${row.override_id}`);
      fetchOverrides(selectedTerm);
      fetchRates(selectedTerm);
    } catch (err) {
      setError(err.response?.data?.message || 'ลบไม่สำเร็จ');
    }
  };

  // ---------- คำนวณบิลใหม่ ----------
  const recalc = async () => {
    if (!window.confirm(
      'คำนวณบรรทัดค่าหอในบิลที่ออกไปแล้วใหม่?\n'
      + 'บรรทัดจากตารางราคา (ค่าเทอม ฯลฯ) และประวัติการชำระไม่ถูกแตะต้อง'
    )) return;
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const res = await axiosInstance.post(`/api/fee/terms/${selectedTerm}/dorm/recalc`);
      if (res.data.success) {
        const r = res.data.data;
        setNotice(
          `ตรวจ ${r.checked} ใบ · ปรับยอด ${r.updated} ใบ · เท่าเดิม ${r.unchanged} ใบ`
          + (r.skipped_below_paid ? ` · ข้าม ${r.skipped_below_paid} ใบ เพราะยอดใหม่ต่ำกว่าที่รับชำระไปแล้ว (ต้องคืนเงิน ให้จัดการเป็นรายกรณี)` : '')
        );
      }
    } catch (err) {
      setError(err.response?.data?.message || 'คำนวณใหม่ไม่สำเร็จ');
    } finally {
      setSaving(false);
    }
  };

  const renderRateCell = (rate, inherited) => {
    if (!rate) {
      return inherited
        ? <span className="text-muted">ตามเขต · {MODES[inherited.charge_mode]?.label}
          {inherited.charge_mode === 'term' ? ` ${baht(inherited.amount)}` : ''}</span>
        : <Badge bg="warning" text="dark">ยังไม่ได้ตั้ง</Badge>;
    }
    return (
      <span>
        <Badge bg={MODES[rate.charge_mode]?.bg}>{MODES[rate.charge_mode]?.label}</Badge>
        {rate.charge_mode === 'term' && <strong className="ms-2">{baht(rate.amount)}</strong>}
        {rate.item_name && <small className="text-muted ms-2">({rate.item_name})</small>}
      </span>
    );
  };

  return (
    <React.Fragment>
      <Header />
      <main className="main main-app p-3 p-lg-4">
        <Container fluid className="px-0">
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
            <div>
              <h2 className="mb-1">ค่าหอพัก</h2>
              <p className="text-muted mb-0">
                ตั้งอัตราที่ระดับเขตเป็นหลัก แล้วลงไปตั้งรายอาคารเมื่ออาคารนั้นเก็บต่างจากเขต
              </p>
            </div>
            <Button variant="outline-primary" onClick={recalc} disabled={!selectedTerm || saving}>
              <i className="ri-refresh-line me-1" />คำนวณค่าหอในบิลใหม่
            </Button>
          </div>

          {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
          {notice && <Alert variant="success" onClose={() => setNotice('')} dismissible>{notice}</Alert>}

          <Card className="border-0 shadow-sm mb-3">
            <Card.Body>
              <Row className="align-items-end g-2">
                <Col md={5}>
                  <Form.Label>เลือกงวด/ภาคเรียน</Form.Label>
                  <Form.Select value={selectedTerm} onChange={(e) => setSelectedTerm(e.target.value)}>
                    <option value="">-- เลือกงวด --</option>
                    {terms.map((t) => (
                      <option key={t.term_id} value={t.term_id}>
                        {t.year_year} / ภาค {t.semester} {t.term_name ? `(${t.term_name})` : ''}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
                {termLabel && (
                  <Col md="auto">
                    <Badge bg="light" text="dark" className="fs-6">งวดที่เลือก: {termLabel}</Badge>
                  </Col>
                )}
              </Row>
            </Card.Body>
          </Card>

          {preview.summary && (
            <Card className="border-0 shadow-sm mb-3">
              <Card.Body>
                <Row className="text-center g-3">
                  <Col xs={6} md={3}>
                    <small className="text-muted d-block">มีทะเบียนหอพัก</small>
                    <h4 className="mb-0">{preview.summary.residents.toLocaleString('th-TH')}</h4>
                  </Col>
                  <Col xs={6} md={3}>
                    <small className="text-muted d-block">มีค่าหอในบิล</small>
                    <h4 className="mb-0 text-primary">{preview.summary.charged.toLocaleString('th-TH')}</h4>
                  </Col>
                  <Col xs={6} md={3}>
                    <small className="text-muted d-block">ฟรี / ยังไม่ได้ตั้งอัตรา</small>
                    <h4 className="mb-0 text-muted">{preview.summary.free_or_unset.toLocaleString('th-TH')}</h4>
                  </Col>
                  <Col xs={6} md={3}>
                    <small className="text-muted d-block">รวมค่าหอที่จะเรียกเก็บ</small>
                    <h4 className="mb-0 text-success">{baht(preview.summary.total_amount)}</h4>
                  </Col>
                </Row>
                <small className="text-muted d-block mt-3">
                  * ตัวเลขคิดจากทะเบียนหอพักที่ยังใช้งานอยู่ตอนนี้ ไม่ใช่ผู้ที่พักอยู่ในภาคเรียนที่เลือก
                </small>
              </Card.Body>
            </Card>
          )}

          <Tabs defaultActiveKey="rates" className="mb-3">
            {/* ---------- อัตราตามเขต/อาคาร ---------- */}
            <Tab eventKey="rates" title="อัตราตามเขต / อาคาร">
              <Card className="border-0 shadow-sm">
                <Card.Header className="bg-white d-flex flex-wrap justify-content-between align-items-center gap-2">
                  <ButtonGroup size="sm">
                    {[
                      { key: 'all', label: 'ทั้งหมด', variant: 'secondary' },
                      { key: 'm', label: 'ชาย', variant: 'primary' },
                      { key: 'f', label: 'หญิง', variant: 'danger' }
                    ].map((g) => (
                      <Button
                        key={g.key}
                        variant={genderFilter === g.key ? g.variant : `outline-${g.variant}`}
                        onClick={() => setGenderFilter(g.key)}
                      >
                        {g.label} <span className="opacity-75">({genderCounts[g.key]})</span>
                      </Button>
                    ))}
                  </ButtonGroup>
                  <div className="d-flex align-items-center gap-2">
                    <small className="text-muted d-none d-lg-inline">
                      คลิกที่ชื่อเขตเพื่อกาง/ยุบรายการอาคาร
                    </small>
                    <Button size="sm" variant="outline-secondary" onClick={expandAll} disabled={!visibleTree.length}>
                      <i className="ri-arrow-down-s-line me-1" />กางทั้งหมด
                    </Button>
                    <Button size="sm" variant="outline-secondary" onClick={collapseAll} disabled={!expanded.size}>
                      <i className="ri-arrow-up-s-line me-1" />ยุบทั้งหมด
                    </Button>
                  </div>
                </Card.Header>
                <Card.Body className="p-0">
                  {loading ? (
                    <div className="text-center py-5"><Spinner animation="border" /></div>
                  ) : !selectedTerm ? (
                    <div className="text-center text-muted py-4">กรุณาเลือกงวด</div>
                  ) : tree.length === 0 ? (
                    <div className="text-center text-muted py-4">ยังไม่มีเขตหอพักในระบบ</div>
                  ) : visibleTree.length === 0 ? (
                    <div className="text-center text-muted py-4">
                      ไม่มีเขต{genderFilter === 'm' ? 'ชาย' : 'หญิง'}ในระบบ
                    </div>
                  ) : (
                    <Table hover responsive className="mb-0 align-middle">
                      <thead className="table-light">
                        <tr>
                          <th>เขต / อาคาร</th>
                          <th className="text-center">ผู้พัก</th>
                          <th>อัตราที่ใช้</th>
                          <th className="text-center">จัดการ</th>
                        </tr>
                      </thead>
                      <tbody>
                        {visibleTree.map((v) => {
                          const isOpen = expanded.has(v.village_id);
                          // อาคารที่ตั้งอัตราเฉพาะหลังไว้จะหายไปตอนยุบ ถ้าไม่บอกไว้ที่แถวเขต
                          // ก็ไม่มีใครรู้ว่ามีราคาที่ไม่ตรงกับเขตซ่อนอยู่ข้างใน
                          const overridden = v.buildings.filter((b) => b.rate).length;
                          return (
                          <React.Fragment key={v.village_id}>
                            <tr
                              className="table-light"
                              style={{ cursor: 'pointer' }}
                              onClick={() => toggleVillage(v.village_id)}
                            >
                              <td>
                                <i className={`${isOpen ? 'ri-arrow-down-s-line' : 'ri-arrow-right-s-line'} me-1`} />
                                <strong>{v.village_name}</strong>
                                <Badge bg={v.village_gender === 'm' ? 'primary' : 'danger'} className="ms-2">
                                  {v.village_gender === 'm' ? 'ชาย' : 'หญิง'}
                                </Badge>
                                <small className="text-muted ms-2">{v.buildings.length} อาคาร</small>
                                {overridden > 0 && (
                                  <Badge bg="secondary" className="ms-2">
                                    ตั้งเฉพาะอาคาร {overridden}
                                  </Badge>
                                )}
                              </td>
                              <td className="text-center text-muted">
                                {v.buildings.reduce((s, b) => s + Number(b.occupied || 0), 0)}
                              </td>
                              <td>{renderRateCell(v.rate, null)}</td>
                              <td className="text-center" onClick={(e) => e.stopPropagation()}>
                                <Button
                                  size="sm" variant="outline-primary" className="me-1"
                                  onClick={() => openRate({
                                    village_id: v.village_id, rate: v.rate, label: v.village_name
                                  })}
                                >
                                  <i className="ri-price-tag-3-line me-1" />
                                  {v.rate ? 'แก้อัตราเขต' : 'ตั้งอัตราเขต'}
                                </Button>
                                {v.rate && (
                                  <Button
                                    size="sm" variant="outline-danger"
                                    onClick={() => deleteRate(v.rate.rate_id, v.village_name)}
                                  >
                                    <i className="ri-delete-bin-line" />
                                  </Button>
                                )}
                              </td>
                            </tr>
                            {isOpen && v.buildings.map((b) => (
                              <tr key={b.dorm_id}>
                                <td className="ps-4">
                                  <i className="ri-corner-down-right-line text-muted me-1" />
                                  {b.dorm_name}
                                  {b.dorm_status !== 'active' && (
                                    <Badge bg="secondary" className="ms-2">ไม่ได้ใช้งาน</Badge>
                                  )}
                                </td>
                                <td className="text-center">{b.occupied} / {b.capacity_total}</td>
                                <td>{renderRateCell(b.rate, v.rate)}</td>
                                <td className="text-center">
                                  <Button
                                    size="sm" variant="outline-secondary" className="me-1"
                                    onClick={() => openRate({
                                      dorm_id: b.dorm_id, rate: b.rate, label: b.dorm_name
                                    })}
                                  >
                                    {b.rate ? 'แก้อัตราอาคาร' : 'ตั้งเฉพาะอาคารนี้'}
                                  </Button>
                                  {b.rate && (
                                    <Button
                                      size="sm" variant="outline-danger"
                                      onClick={() => deleteRate(b.rate.rate_id, b.dorm_name)}
                                    >
                                      <i className="ri-delete-bin-line" />
                                    </Button>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </React.Fragment>
                          );
                        })}
                      </tbody>
                    </Table>
                  )}
                </Card.Body>
              </Card>
            </Tab>

            {/* ---------- ค่าตามจริง (ครอบครอง) ---------- */}
            <Tab eventKey="usage" title={`ค่าตามจริง (ครอบครอง)${meteredBuildings.length ? ` · ${meteredBuildings.length} อาคาร` : ''}`}>
              <Card className="border-0 shadow-sm">
                <Card.Body>
                  {meteredBuildings.length === 0 ? (
                    <div className="text-center text-muted py-4">
                      ยังไม่มีเขต/อาคารที่ตั้งเป็นแบบ &quot;ครอบครอง&quot; ในงวดนี้
                    </div>
                  ) : (
                    <>
                      <Row className="align-items-end g-2 mb-3">
                        <Col md={4}>
                          <Form.Label>อาคาร</Form.Label>
                          <Form.Select
                            value={usageDorm}
                            onChange={(e) => {
                              setUsageDorm(e.target.value);
                              fetchUsage(e.target.value, usageLabel);
                            }}
                          >
                            <option value="">-- เลือกอาคาร --</option>
                            {meteredBuildings.map((b) => (
                              <option key={b.dorm_id} value={b.dorm_id}>
                                {b.village_name} · {b.dorm_name} ({b.occupied} คน)
                              </option>
                            ))}
                          </Form.Select>
                        </Col>
                        <Col md={3}>
                          <Form.Label>หัวข้อค่าใช้จ่าย</Form.Label>
                          <Form.Control
                            list="usage-labels"
                            value={usageLabel}
                            onChange={(e) => setUsageLabel(e.target.value)}
                            onBlur={() => usageDorm && fetchUsage(usageDorm, usageLabel)}
                          />
                          <datalist id="usage-labels">
                            <option value="ค่าไฟ" />
                            <option value="ค่าน้ำ" />
                            <option value="ค่าส่วนกลาง" />
                          </datalist>
                        </Col>
                        <Col md="auto">
                          <Button variant="outline-secondary" onClick={fillAll} disabled={!usageRows.length}>
                            ใส่เท่ากันทุกคน
                          </Button>
                        </Col>
                        <Col md="auto">
                          <Button variant="primary" onClick={saveUsage} disabled={!usageRows.length || saving}>
                            {saving ? 'กำลังบันทึก...' : 'บันทึกทั้งอาคาร'}
                          </Button>
                        </Col>
                      </Row>

                      <Alert variant="light" className="border">
                        <small>
                          หนึ่งหัวข้อ = หนึ่งบรรทัดในบิลของผู้ปกครอง · กรอกซ้ำหัวข้อเดิมคือการแก้ตัวเลขเดิม
                          ไม่ใช่เพิ่มบรรทัดใหม่ · เว้นว่างหรือใส่ 0 = ไม่เก็บหัวข้อนี้กับคนนั้น
                          · บันทึกแล้วต้องกด &quot;คำนวณค่าหอในบิลใหม่&quot; ถ้าออกบิลไปแล้ว
                        </small>
                      </Alert>

                      {usageLoading ? (
                        <div className="text-center py-4"><Spinner animation="border" /></div>
                      ) : usageRows.length === 0 ? (
                        <div className="text-center text-muted py-4">
                          {usageDorm ? 'ไม่มีผู้พักในอาคารนี้' : 'กรุณาเลือกอาคาร'}
                        </div>
                      ) : (
                        <Table hover responsive className="mb-0 align-middle">
                          <thead className="table-light">
                            <tr>
                              <th>เลขประจำตัว</th>
                              <th>ชื่อ-สกุล</th>
                              <th style={{ width: 160 }}>{usageLabel} (บาท)</th>
                              <th>หัวข้ออื่นที่บันทึกไว้</th>
                            </tr>
                          </thead>
                          <tbody>
                            {usageRows.map((s) => (
                              <tr key={s.st_id}>
                                <td>{s.st_idnumber}</td>
                                <td>{s.st_name}</td>
                                <td>
                                  <Form.Control
                                    type="number" min="0" step="0.01" size="sm"
                                    value={usageDraft[s.st_id] ?? ''}
                                    onChange={(e) => setUsageDraft({ ...usageDraft, [s.st_id]: e.target.value })}
                                  />
                                </td>
                                <td>
                                  {s.items.filter((i) => i.usage_label !== usageLabel).map((i) => (
                                    <Badge bg="light" text="dark" className="me-1" key={i.usage_id}>
                                      {i.usage_label} {baht(i.amount)}
                                    </Badge>
                                  ))}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      )}
                    </>
                  )}
                </Card.Body>
              </Card>
            </Tab>

            {/* ---------- ยกเว้นรายคน ---------- */}
            <Tab eventKey="overrides" title={`ยกเว้น / ปรับรายคน${overrides.length ? ` · ${overrides.length}` : ''}`}>
              <Card className="border-0 shadow-sm">
                <Card.Header className="bg-white d-flex justify-content-between align-items-center">
                  <small className="text-muted">
                    ใช้กับทุน เด็กกำพร้า ลูกบุคลากร หรือกรณีที่ตกลงกันเป็นรายๆ — ทับอัตราของเขตและอาคาร
                  </small>
                  <Button
                    size="sm" variant="primary" disabled={!selectedTerm}
                    onClick={() => { setOverrideForm(emptyOverrideForm); setShowOverride(true); }}
                  >
                    <i className="ri-add-line me-1" />เพิ่ม
                  </Button>
                </Card.Header>
                <Card.Body className="p-0">
                  {overrides.length === 0 ? (
                    <div className="text-center text-muted py-4">ยังไม่มีการยกเว้นในงวดนี้</div>
                  ) : (
                    <Table hover responsive className="mb-0 align-middle">
                      <thead className="table-light">
                        <tr>
                          <th>เลขประจำตัว</th>
                          <th>ชื่อ-สกุล</th>
                          <th>หอพัก</th>
                          <th>คิดแบบ</th>
                          <th className="text-end">จำนวนเงิน</th>
                          <th>เหตุผล</th>
                          <th className="text-center">จัดการ</th>
                        </tr>
                      </thead>
                      <tbody>
                        {overrides.map((o) => (
                          <tr key={o.override_id}>
                            <td>{o.st_idnumber}</td>
                            <td>{o.st_name}</td>
                            <td className="text-muted">
                              {o.dorm_name ? `${o.village_name} · ${o.dorm_name}` : 'ไม่มีทะเบียนหอ'}
                            </td>
                            <td><Badge bg={MODES[o.charge_mode]?.bg}>{MODES[o.charge_mode]?.label}</Badge></td>
                            <td className="text-end">
                              {o.charge_mode === 'term'
                                ? (o.override_amount === null ? <span className="text-muted">ตามเขต/อาคาร</span> : baht(o.override_amount))
                                : '-'}
                            </td>
                            <td>{o.override_reason}</td>
                            <td className="text-center">
                              <Button size="sm" variant="outline-danger" onClick={() => deleteOverride(o)}>
                                <i className="ri-delete-bin-line" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </Card.Body>
              </Card>
            </Tab>
          </Tabs>
        </Container>
      </main>

      {/* โมดัลอัตรา */}
      <Modal show={showRate} onHide={() => setShowRate(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>อัตราค่าหอ — {rateForm.scopeLabel}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="g-3">
            <Col xs={12}>
              <Form.Label>รูปแบบการเก็บ</Form.Label>
              {Object.entries(MODES).map(([key, m]) => (
                <Form.Check
                  key={key}
                  type="radio"
                  name="charge_mode"
                  id={`mode-${key}`}
                  checked={rateForm.charge_mode === key}
                  onChange={() => setRateForm({ ...rateForm, charge_mode: key })}
                  label={<span>{m.label} <small className="text-muted">— {m.hint}</small></span>}
                />
              ))}
            </Col>

            {rateForm.charge_mode === 'term' && (
              <Col xs={12}>
                <Form.Label>ค่าหอต่อภาคเรียน (บาท)</Form.Label>
                <Form.Control
                  type="number" min="0" step="0.01"
                  value={rateForm.amount}
                  onChange={(e) => setRateForm({ ...rateForm, amount: e.target.value })}
                />
                <small className="text-muted">แบ่งชำระรายเดือนกำหนดที่หน้า &quot;งวด/ภาคเรียน&quot;</small>
              </Col>
            )}

            {rateForm.charge_mode !== 'free' && (
              <Col xs={12}>
                <Form.Label>ชื่อรายการที่จะขึ้นในบิล (ไม่บังคับ)</Form.Label>
                <Form.Select
                  value={rateForm.tb_fee_item_item_id}
                  onChange={(e) => setRateForm({ ...rateForm, tb_fee_item_item_id: e.target.value })}
                >
                  <option value="">ใช้ชื่อ &quot;ค่าหอพัก&quot;</option>
                  {items.map((i) => (
                    <option key={i.item_id} value={i.item_id}>{i.item_name}</option>
                  ))}
                </Form.Select>
              </Col>
            )}

            <Col xs={12}>
              <Form.Label>หมายเหตุ</Form.Label>
              <Form.Control
                value={rateForm.rate_note}
                onChange={(e) => setRateForm({ ...rateForm, rate_note: e.target.value })}
              />
            </Col>

            {rateForm.dorm_id && (
              <Col xs={12}>
                <Alert variant="light" className="border mb-0">
                  <small>อัตราของอาคารจะ<strong>แทนที่</strong>อัตราของเขต ไม่ใช่เก็บเพิ่มจากเขต</small>
                </Alert>
              </Col>
            )}
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRate(false)}>ยกเลิก</Button>
          <Button variant="primary" onClick={saveRate} disabled={saving}>
            {saving ? 'กำลังบันทึก...' : 'บันทึก'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* โมดัลยกเว้นรายคน */}
      <Modal show={showOverride} onHide={() => setShowOverride(false)} centered>
        <Modal.Header closeButton><Modal.Title>ยกเว้น / ปรับค่าหอรายคน</Modal.Title></Modal.Header>
        <Modal.Body>
          <Row className="g-3">
            <Col xs={12}>
              <Form.Label>ค้นหานักเรียน (เฉพาะผู้ที่มีทะเบียนหอพัก)</Form.Label>
              <div className="d-flex gap-2">
                <Form.Control
                  value={residentSearch}
                  placeholder="เลขประจำตัว หรือ ชื่อ"
                  onChange={(e) => setResidentSearch(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); searchResidents(); } }}
                />
                <Button variant="outline-primary" onClick={searchResidents}>ค้นหา</Button>
              </div>
              {residents.length > 0 && (
                <div className="border rounded mt-2" style={{ maxHeight: 180, overflowY: 'auto' }}>
                  {residents.map((r) => (
                    <button
                      type="button"
                      key={r.st_id}
                      className={`btn btn-sm w-100 text-start border-0 rounded-0 ${String(overrideForm.tb_student_st_id) === String(r.st_id) ? 'btn-primary' : 'btn-light'}`}
                      onClick={() => setOverrideForm({
                        ...overrideForm,
                        tb_student_st_id: r.st_id,
                        studentLabel: `${r.st_name} (${r.st_idnumber}) · ${r.dorm_name}`
                      })}
                    >
                      {r.st_idnumber} — {r.st_name} <small className="text-muted">· {r.dorm_name}</small>
                    </button>
                  ))}
                </div>
              )}
              {overrideForm.studentLabel && (
                <div className="mt-2"><Badge bg="success">{overrideForm.studentLabel}</Badge></div>
              )}
            </Col>

            <Col xs={12}>
              <Form.Label>คิดแบบ</Form.Label>
              <Form.Select
                value={overrideForm.charge_mode}
                onChange={(e) => setOverrideForm({ ...overrideForm, charge_mode: e.target.value })}
              >
                {Object.entries(MODES).map(([key, m]) => (
                  <option key={key} value={key}>{m.label} — {m.hint}</option>
                ))}
              </Form.Select>
            </Col>

            {overrideForm.charge_mode === 'term' && (
              <Col xs={12}>
                <Form.Label>จำนวนเงิน (เว้นว่าง = ใช้ราคาตามเขต/อาคาร)</Form.Label>
                <Form.Control
                  type="number" min="0" step="0.01"
                  value={overrideForm.override_amount}
                  onChange={(e) => setOverrideForm({ ...overrideForm, override_amount: e.target.value })}
                />
              </Col>
            )}

            <Col xs={12}>
              <Form.Label>เหตุผล <span className="text-danger">*</span></Form.Label>
              <Form.Control
                value={overrideForm.override_reason}
                placeholder="เช่น ทุนนักเรียนกำพร้า, บุตรบุคลากร"
                onChange={(e) => setOverrideForm({ ...overrideForm, override_reason: e.target.value })}
              />
              <small className="text-muted">บันทึกลงประวัติการแก้ไขข้อมูลพร้อมชื่อผู้บันทึก</small>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowOverride(false)}>ยกเลิก</Button>
          <Button variant="primary" onClick={saveOverride} disabled={saving}>
            {saving ? 'กำลังบันทึก...' : 'บันทึก'}
          </Button>
        </Modal.Footer>
      </Modal>
    </React.Fragment>
  );
};

export default FeeDormFees;
